import { Examinee, NewExamineeApi } from "@fadmin/tabs/assessments/store/newExamineeSlice";
import { WordResponseType } from "@store/assessmentSlice";
import { OrgLocationApi, getLocationExamineesSortOptions } from "@store/locationsSlice";
import { useCallback, useMemo, useState } from "react";
import { Organization, OrganizationStatus, PersonalInfo } from "../common/helpers/organizationHelper";
import {
    AssessmentDeletionMode,
    AssessmentStatus,
    BulkResponseApi,
    DocumentationCategory,
    getOrganizationsSortOptions,
    getOrganizationUsersSortOptions,
    InviteUser,
    OrganizationUserStatusId,
    PurchaseStatus,
    RoleId,
    SortDirection,
    UserUpdate,
    WordResponseApi
} from "../common/store/generalTypes";
import { User } from "../common/store/userInfoSlice";
import { ApiService, ApiServiceProps } from "./apiService";

export const useApi = (): ApiServiceProps & { loading: boolean } => {
    const methods = ApiService();
    const [loading, setLoading] = useState(false);

    const createOrganization = useCallback(
        async (data: Organization) => {
            setLoading(true);
            return await methods.createOrganization(data).finally(() => setLoading(false));
        },
        [methods.createOrganization]
    );

    const createUser = useCallback(
        async (data: User) => {
            setLoading(true);
            return await methods.createUser(data).finally(() => setLoading(false));
        },
        [methods.createUser]
    );

    const getAssessment = useCallback(
        async (assessmentId: number) => {
            return await methods.getAssessment(assessmentId);
        },
        [methods.getAssessment]
    );

    const getAssessmentForm = useCallback(
        async (assessmentFormId: number, languageId?: number) => {
            return await methods.getAssessmentForm(assessmentFormId, languageId);
        },
        [methods.getAssessmentForm]
    );

    const getAssessmentForms = useCallback(async () => {
        return await methods.getAssessmentForms();
    }, [methods.getAssessmentForms]);

    const initializeAssessment = useCallback(
        async (
            locationId: number,
            examineeId: number,
            languageId: number,
            assessmentFormId: number,
            appointmentId?: number,
            isRetest?: boolean
        ) => {
            setLoading(true);
            return await methods
                .initializeAssessment(locationId, examineeId, languageId, assessmentFormId, appointmentId, isRetest)
                .finally(() => setLoading(false));
        },
        [methods.initializeAssessment]
    );

    const uploadAssessmentResponse = useCallback(
        async (assessmentId: number, word: WordResponseApi) => {
            return await methods.uploadAssessmentResponse(assessmentId, word);
        },
        [methods.uploadAssessmentResponse]
    );

    const uploadAssessmentStepResponse = useCallback(
        async (assessmentId: number, stepName: string, audio: Blob) => {
            return await methods.uploadAssessmentStepResponse(assessmentId, stepName, audio);
        },
        [methods.uploadAssessmentResponse]
    );

    const bulkAssessmentResponse = useCallback(
        async (assessmentId: number, words: BulkResponseApi) => {
            return await methods.bulkAssessmentResponse(assessmentId, words);
        },
        [methods.bulkAssessmentResponse]
    );

    const updateAssessmentStatus = useCallback(
        async (locationId: number, assessmentId: number, status: AssessmentStatus) => {
            setLoading(true);
            return await methods
                .updateAssessmentStatus(locationId, assessmentId, status)
                .finally(() => setLoading(false));
        },
        [methods.updateAssessmentStatus]
    );

    const reviewAssessment = useCallback(
        async (locationId: number, assessmentId: number, note: string) => {
            setLoading(true);
            return await methods.reviewAssessment(locationId, assessmentId, note).finally(() => setLoading(false));
        },
        [methods.reviewAssessment]
    );

    const getOrganization = useCallback(
        async (organizationId: number) => {
            setLoading(true);
            return await methods.getOrganization(organizationId).finally(() => setLoading(false));
        },
        [methods.getOrganization]
    );

    const getOrganizations = useCallback(
        async (
            pageNumber?: number,
            pageSize?: number,
            sortBy?: getOrganizationsSortOptions[],
            sortDirection?: SortDirection,
            search?: string
        ) => {
            setLoading(true);
            return await methods.getOrganizations().finally(() => setLoading(false));
        },
        [methods.getOrganizations]
    );

    const getOrganizationLocations = useCallback(
        async (organizationId: number) => {
            setLoading(true);
            return await methods.getOrganizationLocations(organizationId).finally(() => setLoading(false));
        },
        [methods.getOrganizationLocations]
    );

    const addOrganizationLocation = useCallback(
        async (organizationId: number, orgLocation: OrgLocationApi) => {
            setLoading(true);
            return await methods.addOrganizationLocation(organizationId, orgLocation).finally(() => setLoading(false));
        },
        [methods.addOrganizationLocation]
    );

    const editLocation = useCallback(
        async (locationId: number, orgLocation: OrgLocationApi) => {
            setLoading(true);
            return await methods.editLocation(locationId, orgLocation).finally(() => setLoading(false));
        },
        [methods.editLocation]
    );

    const updateOrganization = useCallback(
        async (organizationId: number, orgName: string) => {
            setLoading(true);
            return await methods.updateOrganization(organizationId, orgName).finally(() => setLoading(false));
        },
        [methods.updateOrganization]
    );

    const updateOrganizationStatus = useCallback(
        async (organizationId: number, status: OrganizationStatus.active | OrganizationStatus.accessRevoked) => {
            setLoading(true);
            return await methods.updateOrganizationStatus(organizationId, status).finally(() => setLoading(false));
        },
        [methods.updateOrganizationStatus]
    );

    const updateOrganizationPrimaryContact = useCallback(
        async (organizationId: number, primaryContact: PersonalInfo) => {
            setLoading(true);
            return await methods
                .updateOrganizationPrimaryContact(organizationId, primaryContact)
                .finally(() => setLoading(false));
        },
        [methods.updateOrganizationPrimaryContact]
    );

    const deleteOrganization = useCallback(
        async (organizationId: number) => {
            setLoading(true);
            return await methods.deleteOrganization(organizationId).finally(() => setLoading(false));
        },
        [methods.deleteOrganization]
    );

    const assignLocationUser = useCallback(
        async (locationId: number, users: InviteUser[]) => {
            setLoading(true);
            return await methods.assignLocationUser(locationId, users).finally(() => setLoading(false));
        },
        [methods.assignLocationUser]
    );

    const batchAssignLocationUser = useCallback(
        async (
            organizationId: number,
            locationId: number,
            users: InviteUser[],
            roleId: RoleId,
            clinicianId?: string
        ) => {
            setLoading(true);
            return await methods
                .batchAssignLocationUser(organizationId, locationId, users, roleId, clinicianId)
                .finally(() => setLoading(false));
        },
        [methods.batchAssignLocationUser]
    );

    const getOrganizationUsers = useCallback(
        async (
            organizationId: number,
            locations?: number[],
            roles?: number[],
            statuses?: OrganizationUserStatusId[],
            pageNumber?: number,
            pageSize?: number,
            search?: string,
            sortBy: getOrganizationUsersSortOptions = "location",
            sortDirection = SortDirection.Ascending
        ) => {
            setLoading(true);
            return await methods
                .getOrganizationUsers(
                    organizationId,
                    locations,
                    roles,
                    statuses,
                    pageNumber,
                    pageSize,
                    search,
                    sortBy,
                    sortDirection
                )
                .finally(() => setLoading(false));
        },
        [methods.getOrganizationUsers]
    );

    const getAppToken = useCallback(async () => {
        setLoading(true);
        return await methods.getAppToken().finally(() => setLoading(false));
    }, [methods.getAppToken]);

    const deleteOrganizationUsers = useCallback(
        async (organizationId: number, userId: string) => {
            setLoading(true);
            return await methods.deleteOrganizationUsers(organizationId, userId).finally(() => setLoading(false));
        },
        [methods.deleteOrganizationUsers]
    );

    const addExaminee = useCallback(
        async (locationId: number, examinee: Examinee) => {
            setLoading(true);
            return await methods.addExaminee(locationId, examinee).finally(() => setLoading(false));
        },
        [methods.addExaminee]
    );

    const setAppointment = useCallback(
        async (locationId: number, examineeId: number, date: Date) => {
            setLoading(true);
            return await methods.setAppointment(locationId, examineeId, date).finally(() => setLoading(false));
        },
        [methods.setAppointment]
    );

    const getLocationExaminees = useCallback(
        async (
            locationId: number,
            pageNumber?: number,
            pageSize?: number,
            search?: string,
            sortBy: getLocationExamineesSortOptions = "lastName",
            sortDirection = SortDirection.Ascending
        ) => {
            setLoading(true);
            return await methods
                .getLocationExaminees(locationId, pageNumber, pageSize, search, sortBy, sortDirection)
                .finally(() => setLoading(false));
        },
        [methods.getLocationExaminees]
    );

    const getUpcomingAssessments = useCallback(
        async (locationId: number) => {
            setLoading(true);
            return await methods.getUpcomingAssessments(locationId).finally(() => setLoading(false));
        },
        [methods.getUpcomingAssessments]
    );

    const getExamineeDetails = useCallback(
        async (locationId: number, examineeId: number) => {
            setLoading(true);
            return await methods.getExamineeDetails(locationId, examineeId).finally(() => setLoading(false));
        },
        [methods.getExamineeDetails]
    );

    const getAssessments = useCallback(
        async (
            locationId: number,
            archived?: boolean,
            status?: AssessmentStatus,
            pageNumber?: number,
            pageSize?: number,
            sortBy?: string
        ) => {
            setLoading(true);
            return await methods
                .getAssessments(locationId, archived, status, pageNumber, pageSize, sortBy)
                .finally(() => setLoading(false));
        },
        [methods.getAssessments]
    );

    const getAssessmentResults = useCallback(
        async (locationId: number, assessmentId: number) => {
            setLoading(true);
            return await methods.getAssessmentResults(locationId, assessmentId).finally(() => setLoading(false));
        },
        [methods.getAssessmentResults]
    );

    const getAssessmentHistoryByExaminee = useCallback(
        async (locationId: number, examineeId: number) => {
            setLoading(true);
            return await methods
                .getAssessmentHistoryByExaminee(locationId, examineeId)
                .finally(() => setLoading(false));
        },
        [methods.getAssessments]
    );

    const downloadAssessments = useCallback(
        async (locationId: number, assessmentIds: number[], fileFormat?: "csv") => {
            setLoading(true);
            return await methods
                .downloadAssessments(locationId, assessmentIds, fileFormat)
                .finally(() => setLoading(false));
        },
        [methods.downloadAssessments]
    );

    const deleteAssessments = useCallback(
        async (locationId: number, assessmentIds: number[], deletionMode?: AssessmentDeletionMode) => {
            setLoading(true);
            return await methods
                .deleteAssessments(locationId, assessmentIds, deletionMode)
                .finally(() => setLoading(false));
        },
        [methods.deleteAssessments]
    );

    const updateExaminee = useCallback(
        async (locationId: number, examinee: number, details: NewExamineeApi) => {
            setLoading(true);
            return await methods.updateExaminee(locationId, examinee, details).finally(() => setLoading(false));
        },
        [methods.updateExaminee]
    );

    const getMyPermission = useCallback(async () => {
        setLoading(true);
        return await methods.getMyPermission().finally(() => setLoading(false));
    }, [methods.getMyPermission]);

    const getUser = useCallback(
        async (email: string) => {
            setLoading(true);
            return await methods.getUser(email).finally(() => setLoading(false));
        },
        [methods.getUser]
    );

    const deleteUserRoleByLocation = useCallback(
        async (userId: string, roleId: number, locationId: number) => {
            setLoading(true);
            return await methods.deleteUserRoleByLocation(userId, roleId, locationId).finally(() => setLoading(false));
        },
        [methods.deleteUserRoleByLocation]
    );

    const getAssessmentsPricings = useCallback(async () => {
        setLoading(true);
        return await methods.getAssessmentsPricings().finally(() => setLoading(false));
    }, [methods.getAssessmentsPricings]);

    const createPurchase = useCallback(
        async (
            organizationId: number,
            assessmentQuantity: number,
            offline?: boolean,
            unitPrice?: number,
            discountPercentage?: number
        ) => {
            setLoading(true);
            return await methods
                .createPurchase(organizationId, assessmentQuantity, offline, unitPrice, discountPercentage)
                .finally(() => setLoading(false));
        },
        [methods.createPurchase]
    );

    const getPurchases = useCallback(
        async (organizationId: number, pageNumber?: number, pageSize?: number, statuses?: PurchaseStatus[]) => {
            setLoading(true);
            return await methods
                .getPurchases(organizationId, pageNumber, pageSize, statuses)
                .finally(() => setLoading(false));
        },
        [methods.getPurchases]
    );

    const updateTargetResponse = useCallback(
        async (assessmentId: number, targetResponseId: number, responseType: WordResponseType) => {
            setLoading(true);
            return await methods
                .updateTargetResponse(assessmentId, targetResponseId, responseType)
                .finally(() => setLoading(false));
        },
        [methods.updateTargetResponse]
    );

    const updateAssessmentResult = useCallback(
        async (locationId: number, assessmentId: number) => {
            setLoading(true);
            return await methods.updateAssessmentResult(locationId, assessmentId).finally(() => setLoading(false));
        },
        [methods.updateAssessmentResult]
    );

    const getAvailableAssessments = useCallback(
        async (organizationId: number) => {
            setLoading(true);
            return await methods.getAvailableAssessments(organizationId).finally(() => setLoading(false));
        },
        [methods.getAvailableAssessments]
    );

    const updateUser = useCallback(
        async (email: string, user: UserUpdate) => {
            setLoading(true);
            return await methods.updateUser(email, user).finally(() => setLoading(false));
        },
        [methods.updateUser]
    );

    const updateLocationRole = useCallback(
        async (email: string, organizationId: number, locationId: number, roleId: number) => {
            setLoading(true);
            return await methods
                .updateLocationRole(email, organizationId, locationId, roleId)
                .finally(() => setLoading(false));
        },
        [methods.updateLocationRole]
    );

    const createTargetResponseNote = useCallback(
        async (assessmentId: number, targetResponseId: number, note: string) => {
            setLoading(true);
            return await methods
                .createTargetResponseNote(assessmentId, targetResponseId, note)
                .finally(() => setLoading(false));
        },
        [methods.createTargetResponseNote]
    );

    const sendEmailContactSales = useCallback(
        async (
            organization: Organization,
            user: User,
            title: string,
            jobTitle: string,
            country: string,
            details: string
        ) => {
            setLoading(true);
            return await methods
                .sendEmailContactSales(organization, user, title, jobTitle, country, details)
                .finally(() => setLoading(false));
        },
        [methods.sendEmailContactSales]
    );

    const getDocumentations = useCallback(async () => {
        setLoading(true);
        return await methods.getDocumentations().finally(() => setLoading(false));
    }, [methods.getDocumentations]);

    const createDocumentation = useCallback(
        async (title: string, description: string, type: DocumentationCategory, file?: File) => {
            setLoading(true);
            return await methods.createDocumentation(title, description, type, file).finally(() => setLoading(false));
        },
        [methods.createDocumentation]
    );

    const connectionTest = useCallback(async () => {
        return await methods.connectionTest();
    }, [methods.connectionTest]);

    const getWordRequests = useCallback(
        async (pageNumber?: number, pageSize?: number) => {
            return await methods.getWordRequests(pageNumber, pageSize);
        },
        [methods.getWordRequests]
    );

    const setAlternateForWord = useCallback(
        async (word: string, newAlternate: string) => {
            setLoading(true);
            return await methods.setAlternateForWord(word, newAlternate).finally(() => setLoading(false));
        },
        [methods.setAlternateForWord]
    );

    const getRoles = useCallback(async () => {
        setLoading(true);
        return await methods.getRoles().finally(() => setLoading(false));
    }, [methods.getRoles]);

    const getAgreements = useCallback(async () => {
        setLoading(true);
        return await methods.getAgreements().finally(() => setLoading(false));
    }, [methods.getAgreements]);

    const createUserAgreement = useCallback(
        async (agreementId: number, userId: string, locationId: number, roleId: number) => {
            setLoading(true);
            return await methods
                .createUserAgreement(agreementId, userId, locationId, roleId)
                .finally(() => setLoading(false));
        },
        [methods.createUserAgreement]
    );

    return useMemo(
        () => ({
            loading,
            createUser,
            createOrganization,
            getAssessmentForm,
            getAssessmentForms,
            uploadAssessmentResponse,
            uploadAssessmentStepResponse,
            bulkAssessmentResponse,
            getAssessment,
            initializeAssessment,
            updateAssessmentStatus,
            reviewAssessment,
            getOrganization,
            getOrganizations,
            getOrganizationLocations,
            addOrganizationLocation,
            editLocation,
            updateOrganization,
            updateOrganizationStatus,
            updateOrganizationPrimaryContact,
            deleteOrganization,
            assignLocationUser,
            getOrganizationUsers,
            getAppToken,
            batchAssignLocationUser,
            deleteOrganizationUsers,
            addExaminee,
            setAppointment,
            getLocationExaminees,
            getUpcomingAssessments,
            getExamineeDetails,
            getAssessments,
            getAssessmentResults,
            updateExaminee,
            getAssessmentHistoryByExaminee,
            downloadAssessments,
            deleteAssessments,
            getMyPermission,
            deleteUserRoleByLocation,
            getAssessmentsPricings,
            createPurchase,
            getPurchases,
            getUser,
            updateTargetResponse,
            updateAssessmentResult,
            getAvailableAssessments,
            updateUser,
            updateLocationRole,
            createTargetResponseNote,
            sendEmailContactSales,
            getDocumentations,
            createDocumentation,
            connectionTest,
            getWordRequests,
            setAlternateForWord,
            getRoles,
            getAgreements,
            createUserAgreement
        }),
        [
            loading,
            createUser,
            createOrganization,
            getAssessment,
            getAssessmentForm,
            getAssessmentForms,
            initializeAssessment,
            uploadAssessmentResponse,
            uploadAssessmentStepResponse,
            bulkAssessmentResponse,
            updateAssessmentStatus,
            reviewAssessment,
            getOrganization,
            getOrganizations,
            getOrganizationLocations,
            addOrganizationLocation,
            editLocation,
            updateOrganization,
            updateOrganizationStatus,
            updateOrganizationPrimaryContact,
            deleteOrganization,
            assignLocationUser,
            getOrganizationUsers,
            getAppToken,
            batchAssignLocationUser,
            deleteOrganizationUsers,
            addExaminee,
            setAppointment,
            getLocationExaminees,
            getUpcomingAssessments,
            getExamineeDetails,
            getAssessments,
            getAssessmentResults,
            updateExaminee,
            getAssessmentHistoryByExaminee,
            downloadAssessments,
            deleteAssessments,
            getMyPermission,
            deleteUserRoleByLocation,
            getAssessmentsPricings,
            createPurchase,
            getPurchases,
            getUser,
            updateTargetResponse,
            updateAssessmentResult,
            getAvailableAssessments,
            updateUser,
            updateLocationRole,
            createTargetResponseNote,
            sendEmailContactSales,
            getDocumentations,
            createDocumentation,
            connectionTest,
            getWordRequests,
            setAlternateForWord,
            getRoles,
            getAgreements,
            createUserAgreement
        ]
    );
};
